import styled from '@emotion/styled';
import Box from '@mui/material/Box';
import { theme } from '@ui/theme/cobrainer-theme';
import { Fragment, Suspense, useContext } from 'react';
import { Outlet } from 'react-router';

import { AppBar } from './app-bar/AppBar';
import { NavigationMenuProvider } from './app-bar/menu-bar-v2/NavigationMenuContext';
import { shouldRenderOnboarding } from './app-base-conditions';
import { ConsentDialog } from './consent-dialog/ConsentDialog';
import { PageErrorBoundary } from './error-handling/ErrorBoundaries';
import { AppDrawers } from './pages/app-drawers';
import { route as onboardingRoutes } from './pages/onboarding/constants';
import { OnboardingForwarder } from './pages/onboarding/OnboardingForwarder';
import { ProfilePreview } from './pages/profile/profile-preview';
import { ApplicationContext } from '../../../shared/source/context/context';
import { useConsentQuery } from '../../../shared/source/query-hooks/consent';
import { PageLoadingSpinner } from '../design/components/LoadingSpinner';
import { SideDrawerNavigationV2 } from './app-bar/menu-bar-v2/NavigationMenuV2';

import type { FunctionComponent } from 'react';

const LayoutGrid = styled.div({
    backgroundColor: theme.colors.gray[200],
    display: 'grid',
    flex: 1,
    gridTemplateColumns: 'auto 1fr',
});

const MainContent = styled.div<{ readonly hasNavigationMenu?: boolean }>(({ hasNavigationMenu = true }) => ({
    backgroundColor: theme.colors.white[200],
    borderRadius: theme.borderRadius.m,
    marginBottom: hasNavigationMenu ? theme.spacing.m : 0,
    marginRight: hasNavigationMenu ? theme.spacing.m : 0,
    minWidth: 0,
    overflow: 'hidden',
}));

type LayoutProps = {
    readonly hasNavigationMenu?: boolean;
};

export const Layout: FunctionComponent<LayoutProps> = ({ hasNavigationMenu = true }) => {
    const { applicationState } = useContext(ApplicationContext);
    const { myUserProfile } = applicationState;
    const { data: consentData } = useConsentQuery();
    const { pathname } = applicationState.location;

    if (myUserProfile.isNothing) {
        return <PageLoadingSpinner />;
    }

    if (
        shouldRenderOnboarding({
            consentStatus: consentData?.status,
            onboardingRoutesPath: onboardingRoutes.patterns.onboarding,
            pathname,
            userProfileExists: myUserProfile.value.exists === true,
        })
    ) {
        return (
            <Fragment>
                <OnboardingForwarder />
                <ConsentDialog />
            </Fragment>
        );
    }

    return (
        <NavigationMenuProvider>
            <Fragment>
                <AppBar />
                <Box component="main" flex={1} sx={{ display: 'flex', position: 'relative' }}>
                    <PageErrorBoundary>
                        <Suspense fallback={<PageLoadingSpinner />}>
                            <LayoutGrid>
                                {hasNavigationMenu ? <SideDrawerNavigationV2 /> : <span />}
                                <MainContent hasNavigationMenu={hasNavigationMenu}>
                                    <Outlet />
                                </MainContent>
                            </LayoutGrid>
                        </Suspense>
                        <AppDrawers />
                        <ProfilePreview />
                    </PageErrorBoundary>
                </Box>
            </Fragment>
        </NavigationMenuProvider>
    );
};
